/**
 * kSettleOnce — the "answer the caller EXACTLY once" contract as an object, instead of the
 * `const Cb = this.Done; this.Done = Noop; Cb(x)` triplet that was hand-written fourteen times
 * across the Teams-approval layer (and drifted: some copies released the latch, some did not).
 *
 *   const Done: kSettleOnce<boolean> = new kSettleOnce<boolean>(null);
 *   Done.Arm(Callback);            // in Execute / Begin
 *   Done.Settle(true);             // anywhere — the second call is a no-op and answers false
 *   Done.IsPending()               // true until settled
 *
 * Settle is deliberately NOT gated: it is the safe-default path itself (a failure answers the caller
 * with the failure value), and a gate here would turn every reported error into a hang — exactly the
 * 120 s timeout that kFunctionErrorHandler.Settle exists to prevent.
 */
export class kSettleOnce<T> {
    private Callback: ((Value: T) => void) | null;

    public constructor(Callback: ((Value: T) => void) | null) {
        this.Callback = Callback;
    }

    /** DOCUMENTED EXCEPTION: a plain assignment — arming cannot fail and must work gate up or not. */
    public Arm = (Callback: ((Value: T) => void) | null): void => {
        this.Callback = Callback;
    };

    /** DOCUMENTED EXCEPTION: runs gate up or not (it IS the safe-default path). Answers whether the
        call was the one that settled; a throw inside the callback is the CALLER's, not this class's. */
    public Settle = (Value: T): boolean => {
        const Cb: ((Value: T) => void) | null = this.Callback;
        this.Callback = null;
        if (Cb === null) { return false; }
        Cb(Value);
        return true;
    };

    /** DOCUMENTED EXCEPTION: pure reader. */
    public IsPending = (): boolean => {
        return this.Callback !== null;
    };
}

/**
 * kSettleEither — the TWO-outcome form of kSettleOnce: answer the caller EXACTLY once, through the success
 * callback OR the failure callback, per arming (R6, 2026-10-10). Lifted from three hand-written copies that
 * each kept their own flag: kSPBatchReadCore (_Open/_Claim/_Deliver/_Release), kSPBatchWriteCore
 * (_Open/Claim/Release) and kGraphFileUploader (Answered/FireSuccess/FireFailure).
 *
 *   const Answer: kSettleEither<number[], void> = new kSettleEither<number[], void>(OnStatuses, OnFailed);
 *   Answer.Rearm();                // in Execute: this run owes its caller one answer (reusable instance)
 *   Answer.Succeed(Statuses);      // or Answer.Fail(); the first one wins, every later call answers false
 *   Answer.IsPending()             // true while the answer is still owed
 *
 * Differences from kSettleOnce, on purpose: the constructor STORES the callbacks but does not open the latch
 * (the classes it replaces owe nothing until Execute), and Rearm re-opens the same callbacks for the next run.
 * Same rules as kSettleOnce: ungated (it IS the safe-default path), the latch closes BEFORE the callback runs,
 * and a throw inside a callback is the CALLER's - it propagates, and no second answer follows.
 * Policy such as "a success under a latched gate answers failure" stays with the caller.
 */
export class kSettleEither<TSuccess, TFailure> {
    private OnSuccess: ((Value: TSuccess) => void) | null;
    private OnFailure: ((Value: TFailure) => void) | null;
    private Open: boolean = false;

    public constructor(OnSuccess: ((Value: TSuccess) => void) | null, OnFailure: ((Value: TFailure) => void) | null) {
        this.OnSuccess = OnSuccess;
        this.OnFailure = OnFailure;
    }

    /** DOCUMENTED EXCEPTION: plain assignments - arming cannot fail and must work gate up or not. Replaces both callbacks and opens. */
    public Arm = (OnSuccess: ((Value: TSuccess) => void) | null, OnFailure: ((Value: TFailure) => void) | null): void => {
        this.OnSuccess = OnSuccess;
        this.OnFailure = OnFailure;
        this.Open = true;
    };

    /** DOCUMENTED EXCEPTION: a plain assignment. Re-opens the stored callbacks: the next run owes one answer. */
    public Rearm = (): void => {
        this.Open = true;
    };

    /** DOCUMENTED EXCEPTION: ungated (the safe-default path). Answers whether this call was the one that settled. */
    public Succeed = (Value: TSuccess): boolean => {
        if (this.Claim() === false) { return false; }
        if (this.OnSuccess !== null) { this.OnSuccess(Value); }
        return true;
    };

    /** DOCUMENTED EXCEPTION: ungated (the safe-default path). Answers whether this call was the one that settled. */
    public Fail = (Value: TFailure): boolean => {
        if (this.Claim() === false) { return false; }
        if (this.OnFailure !== null) { this.OnFailure(Value); }
        return true;
    };

    /** DOCUMENTED EXCEPTION: pure reader. */
    public IsPending = (): boolean => {
        return this.Open;
    };

    /** DOCUMENTED EXCEPTION: a pure flag swap - true once per arming, false after. */
    private Claim = (): boolean => {
        if (this.Open === false) { return false; }
        this.Open = false;
        return true;
    };
}
